import { useEffect, useState, useMemo } from "react";
import { useProblem, useS3Client, useDynamoClient } from "@/hooks/contextHooks";
import { AppMeta } from "@xnav/types/src/common.ts"
import { apiDeleteRequest, apiGetRequest, apiPostRequest, apiPutRequest } from "@/functions/requests";
import { Box, Center, Flex, Heading, Text, Skeleton, Stack, HStack, Badge, useToast, SimpleGrid, IconButton, useColorModeValue, Menu, MenuButton, MenuList, MenuItem, Button, Portal, Input, InputGroup, InputLeftElement, Tabs, TabList, Tab } from "@chakra-ui/react";
import AppCard from "@/components/appCard";
import AddAppButton from "@/components/addAppButton";
import FuzzyFinder, { FuzzyFindWrapperProps } from "@/components/fuzzyFinder";
import Loading from "@/components/loading";
import { GetObjectCommand, HeadObjectCommand, S3Client, ListObjectsV2Command, PutObjectCommand, CopyObjectCommand, DeleteObjectsCommand } from "@aws-sdk/client-s3";
import { GetItemCommand } from "@aws-sdk/client-dynamodb";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faThLarge, faSearch, faFilter, faSortAmountDown, faBullhorn, faImage, faBoxArchive, faBookOpen, faPlus } from "@fortawesome/free-solid-svg-icons";

import { motion, AnimatePresence } from "framer-motion";

const MotionFlex = motion(Flex);
const MotionSimpleGrid = motion(SimpleGrid);

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1
		}
	}
};

const item = {
	hidden: { opacity: 0, y: 20 },
	show: { opacity: 1, y: 0 }
};

const ListWrapper = <T,>({ data, renderComponentProps, RenderComponent }: FuzzyFindWrapperProps<T>) => (
	<Box
		w="full"
		bg={useColorModeValue("white", "#101622")}
		borderRadius="xl"
		border="1px solid"
		borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.06)")}
		boxShadow="sm"
		overflow="hidden"
	>
		{data.length > 0 && (
			<Flex
				px={4}
				py={3}
				bg={useColorModeValue("gray.50", "rgba(255, 255, 255, 0.02)")}
				borderBottom="1px solid"
				borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.05)")}
				display={{ base: 'none', lg: 'flex' }}
				color={useColorModeValue("gray.500", "gray.400")}
				fontSize="11px"
				fontWeight="bold"
				letterSpacing="wider"
				textTransform="uppercase"
				align="center"
				gap={4}
			>
				<Box flex={1} minW="260px">Course</Box>
				<Box w="140px" display={{ base: 'none', md: 'block' }}>Instructor</Box>
				<Box w="100px" display={{ base: 'none', lg: 'block' }}>Updated</Box>
				<Box w="90px" display={{ base: 'none', xl: 'block' }}>Size</Box>
				<Box w="130px" textAlign="center">Status</Box>
				<Box w="120px" textAlign="right">Actions</Box>
			</Flex>
		)}
		<MotionFlex
			flex={1}
			flexDir='column'
			variants={container}
			initial="hidden"
			animate="show"
			w="full"
		>
			{data.map((d, i) => (
				<motion.div key={i} variants={item}>
					<RenderComponent data={d} {...renderComponentProps} />
				</motion.div>
			))}
			{data.length === 0 && (
				<Flex py={12} justify="center" align="center" flexDir="column" gap={2}>
					<Text color={useColorModeValue("gray.500", "gray.400")} fontSize="sm" fontWeight="medium">
						No courses found matching your criteria.
					</Text>
				</Flex>
			)}
		</MotionFlex>
	</Box>
)

const GridWrapper = <T,>({ data, renderComponentProps, RenderComponent }: FuzzyFindWrapperProps<T>) => (
	<MotionSimpleGrid
		columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
		spacing={4}
		p={3}
		variants={container}
		initial="hidden"
		animate="show"
	>
		{data.map((d, i) => (
			<motion.div key={i} variants={item} style={{ height: '100%' }}>
				<RenderComponent data={d} {...renderComponentProps} />
			</motion.div>
		))}
	</MotionSimpleGrid>
)

import { useOutletContext } from "react-router-dom";

const sortOptionLabels: Record<string, string> = {
	Recent: 'Recently Opened',
	Edited: 'Recently Edited',
	Deployed: 'Recently Deployed',
	Date: 'Recently Created',
	Size: 'Size',
	Category: 'Category',
	Type: 'Type'
}

export default function Home() {
	const [, setProblem] = useProblem()
	const [apps, setApps] = useState<AppMeta[]>()
	
	const [searchQuery, setSearchQuery] = useState('')
	const [sortOption, setSortOption] = useState('Recent')
	const [viewMode, setViewMode] = useState<'list' | 'grid' | 'landscape'>('list')
	const [showAnimation, setShowAnimation] = useState(true)
	const [broadcast, setBroadcast] = useState<{ message: string, isActive: boolean } | null>(null)

	useEffect(() => {
		const fetchBroadcast = async () => {
			try {
				const data = await apiGetRequest<undefined, { message: string, isActive: boolean }>('/users/system_broadcast');
				if (data && data.isActive) {
					setBroadcast(data);
				}
			} catch (e) {
				console.error("Failed to fetch broadcast", e);
			}
		}
		fetchBroadcast();

		const timer = setTimeout(() => {
			setShowAnimation(false)
		}, 2500)
		return () => clearTimeout(timer)
	}, [])

	const toast = useToast()

	const s3 = useS3Client() as S3Client
	const dynamoClient = useDynamoClient()
	const { user } = useAuthenticator()
	const [userRole, setUserRole] = useState<string | null>(null);
	const [userOrg, setUserOrg] = useState<string | null>(null);

	useEffect(() => {
		const fetchRole = async () => {
			if (user?.signInDetails?.loginId) {
				try {
					const data = await apiGetRequest<undefined, { role: string; org?: string }>(`/users/${user.signInDetails.loginId}`);
					setUserRole(data?.role || 'Instructor');
					setUserOrg(data?.org || null);
				} catch (error) {
					console.error("Failed to fetch user role", error);
					setUserRole('Instructor');
					setUserOrg(null);
				}
			}
		};
		fetchRole();
	}, [user]);

	function validateApp(newApp: AppMeta): string | undefined {
		const compApps = apps!.filter(a => a.id !== newApp.id)
		for (const a of compApps) {
			console.log("New Name: ", newApp.name)
			console.log("Comp Name: ", a.name)
			if (a.name === newApp.name) return "App name must be unique"
			if (newApp.hiddenCode && a.hiddenCode && a.hiddenCode.trim().toLowerCase() === newApp.hiddenCode.trim().toLowerCase()) {
				return "Access code must be unique across all courses"
			}
		}
		return undefined
	}

	async function fetchApps() {
		try {
			let tempApps: AppMeta[] = [];
			
			tempApps = await apiGetRequest<string, AppMeta[]>('/apps');
			
			setApps(tempApps)
			console.log(tempApps)
			const iconAppsPromises = tempApps.map(async (app) => {
				const command = new GetObjectCommand({
					Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
					Key: `${app.id}/icon`
				})
				console.log(`${app.id}/icon`)
				const headCommand = new HeadObjectCommand({
					Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
					Key: `${app.id}/icon`
				})

				// Fetch Icon URL
				let iconUrl = '/default.png'
				try {
					await s3.send(headCommand)
					iconUrl = await getSignedUrl(s3 as any, command as any, { expiresIn: 3600 })
				} catch (e) {
					console.log(e)
				}

				// Calculate App Size
				let totalSize = 0;
				try {
					let isTruncated = true;
					let continuationToken: string | undefined = undefined;
					while (isTruncated) {
						const listCommand = new ListObjectsV2Command({
							Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
							Prefix: `${app.id}/`,
							ContinuationToken: continuationToken
						});
						const listResult = await s3.send(listCommand);
						if (listResult.Contents) {
							totalSize += listResult.Contents.reduce((acc, obj) => acc + (obj.Size || 0), 0);
						}
						isTruncated = listResult.IsTruncated ?? false;
						continuationToken = listResult.NextContinuationToken;
					}
				} catch (e) {
					console.error("Error calculating size for app:", app.name, e);
				}

				// Preload Course Content Stats (Videos, Images, PDFs)
				let videos = 0;
				let images = 0;
				let pdfs = 0;

				try {
					const metaCmd = new GetObjectCommand({
						Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
						Key: `${app.id}/media/meta.json`
					});
					const metaUrl = await getSignedUrl(s3 as any, metaCmd as any, { expiresIn: 3600 });
					const r = await fetch(metaUrl);
					if (r.ok) {
						const mediaList = await r.json();
						if (Array.isArray(mediaList)) {
							for (const m of mediaList) {
								if (m.type === 0 || m.type === 'video') videos += 1;
								else if (m.type === 3 || m.type === 'image') images += 1;
								else if (m.type === 2 || m.type === 'pdf') pdfs += 1;
							}
						}
					}
				} catch (e) {
					// Fallback to 0 if no media meta
				}

				return {
					...app,
					iconUrl: iconUrl,
					size: totalSize,
					stats: { folders: 0, videos, images, pdfs }
				}
			})
			const iconApps = await Promise.all(iconAppsPromises)

			// Deduplicate apps by ID, keeping the one with actual content (size > 0)
			const uniqueAppsMap = new Map<string, AppMeta>();
			for (const app of iconApps) {
				const existing = uniqueAppsMap.get(app.id);
				if (!existing || (app.size && app.size > 0)) {
					uniqueAppsMap.set(app.id, app);
				}
			}
			
			setApps(Array.from(uniqueAppsMap.values()))
		} catch (e: any) {
			if (e.name === 'NoSuchKey') setApps([])
			else throw e
		}
	}

	async function postApp(app: AppMeta, file: File | undefined) {
		if (!apps) return
		const toastRef = toast({
			title: `Creating ${app.name}`,
			description: 'Please wait...',
			status: 'warning',
			position: 'bottom-left',
			isClosable: true,
			duration: null
		})
		await apiPostRequest(`apps`, app)

		if (file) {
			const iconPutUrl = await apiPutRequest<undefined, string>(`/appIcon/${app.id}`, undefined)
			console.log(iconPutUrl)
			await axios.put(iconPutUrl, file)
		}
		setApps(apps => [...apps!, app])
		toast.close(toastRef)
	}

	function deleteApp(app: AppMeta) {
		if (!apps) return
		const toastRef = toast({
			title: `Deleting ${app.name}`,
			description: 'Please wait...',
			status: 'warning',
			position: 'bottom-left',
			isClosable: true
		})
		const deleteAppsPromises = [
			apiDeleteRequest<undefined>(`/deploy/${app.id}`),
			apiDeleteRequest<AppMeta>(`/apps/${app.id}`)
		]
		Promise.all(deleteAppsPromises).then(() => {
			setApps(apps.filter(a => a.id !== app.id))
			toast.close(toastRef)
		}).catch((err) => {
			setProblem(err)
			toast.close(toastRef)
		})
	}

	async function archiveApp(app: AppMeta) {
		if (!apps) return
		const toastRef = toast({
			title: `Archiving ${app.name}`,
			description: 'Please wait...',
			status: 'info',
			position: 'bottom-left',
			isClosable: true
		})
		try {
			if (app.deployed) {
				await apiDeleteRequest(`/deploy/${app.id}`).catch(() => {})
			}
			const updatedApp = {
				...app,
				archived: true,
				edited: new Date().toString()
			}
			await apiPutRequest(`apps/${app.id}`, updatedApp)
			setApps(apps => apps!.map(a => a.id === app.id ? updatedApp : a))
			toast({
				title: `${app.name} Archived`,
				description: 'Moved to Archive tab. You can restore it anytime.',
				status: 'success',
				duration: 3000,
				isClosable: true
			})
		} catch (err) {
			setProblem(err)
		} finally {
			toast.close(toastRef)
		}
	}

	async function unarchiveApp(app: AppMeta) {
		if (!apps) return
		const toastRef = toast({
			title: `Restoring ${app.name}`,
			description: 'Please wait...',
			status: 'info',
			position: 'bottom-left',
			isClosable: true
		})
		try {
			const updatedApp = {
				...app,
				archived: false,
				edited: new Date().toString()
			}
			await apiPutRequest(`apps/${app.id}`, updatedApp)
			setApps(apps => apps!.map(a => a.id === app.id ? updatedApp : a))
			toast({
				title: `${app.name} Restored`,
				description: 'Course moved back to Active Courses.',
				status: 'success',
				duration: 3000,
				isClosable: true
			})
		} catch (err) {
			setProblem(err)
		} finally {
			toast.close(toastRef)
		}
	}

	async function patchApp(newApp: AppMeta, file: File | undefined) {
		const toastRef = toast({
			title: `Updating ${newApp.name}`,
			description: 'Please wait...',
			status: 'warning',
			position: 'bottom-left',
			isClosable: true
		})
		
		const updatedApp = {
			...newApp,
			edited: new Date().toString()
		}
		let url = `apps/${updatedApp.id}`;
		await apiPutRequest(url, updatedApp);

		if (file) {
			const putAppIconUrl = await apiPutRequest<{ contentType: string }, string>(
				`/appIcon/${updatedApp.id}`,
				{ contentType: file.type }
			)
			await axios.put(putAppIconUrl, file)
		}
		setApps(apps => apps!.map(a => a.id === updatedApp.id ? updatedApp : a))
		toast.close(toastRef)
	}

	const [users, setUsers] = useState<{ org: string }[]>([])

	async function fetchUsers() {
		try {
			const data = await apiGetRequest<undefined, { org: string }[]>('/users')
			setUsers(data || [])
		} catch (e) {
			console.error("Error fetching users for orgs", e)
		}
	}

	useEffect(() => {
		if (userRole !== null) {
			fetchApps()
			fetchUsers()
		}
	}, [userRole])

	const [orgFilter, setOrgFilter] = useState<string>("All")
	const [orgSearchQuery, setOrgSearchQuery] = useState("")
	const [courseTab, setCourseTab] = useState<'active' | 'archive'>('active')

	const activeAppsCount = useMemo(() => (apps || []).filter(a => !a.archived).length, [apps])
	const archivedAppsCount = useMemo(() => (apps || []).filter(a => a.archived === true).length, [apps])

	const filteredAndSortedApps = useMemo(() => {
		if (!apps) return [];
		let filtered = apps;

		if (courseTab === 'active') {
			filtered = filtered.filter(a => !a.archived);
		} else {
			filtered = filtered.filter(a => a.archived === true);
		}

		if (orgFilter !== "All") {
			const lowerFilter = orgFilter.toLowerCase();
			filtered = filtered.filter(a => 
				(a.org && a.org.trim().toLowerCase().includes(lowerFilter)) || 
				(a.orgId && a.orgId.trim().toLowerCase().includes(lowerFilter))
			);
		}

		return [...filtered].sort((a, b) => {
			if (sortOption === 'Recent') {
				const openedStr = localStorage.getItem('recentlyOpenedCourses');
				let opened: string[] = [];
				try {
					opened = openedStr ? JSON.parse(openedStr) : [];
				} catch (e) {}
				const indexA = opened.indexOf(a.id);
				const indexB = opened.indexOf(b.id);
				
				if (indexA !== -1 && indexB !== -1) {
					return indexA - indexB;
				}
				if (indexA !== -1) return -1;
				if (indexB !== -1) return 1;
				return new Date(b.edited || 0).getTime() - new Date(a.edited || 0).getTime();
			}
			if (sortOption === 'Edited') {
				return new Date(b.edited || 0).getTime() - new Date(a.edited || 0).getTime();
			}
			if (sortOption === 'Deployed') {
				return new Date(b.deployed || 0).getTime() - new Date(a.deployed || 0).getTime();
			}
			if (sortOption === 'Size') return (b.size || 0) - (a.size || 0);
			if (sortOption === 'Category') return (a.category || '').localeCompare(b.category || '');
			if (sortOption === 'Type') return (a.courseType || '').localeCompare(b.courseType || '');
			return new Date(b.created).getTime() - new Date(a.created).getTime();
		})
	}, [apps, sortOption, orgFilter, courseTab])

	const uniqueOrgs = useMemo(() => {
		const orgsMap = new Map<string, string>();
		users.forEach(u => {
			if (u.org && u.org.trim() !== "") {
				const lower = u.org.trim().toLowerCase();
				if (!orgsMap.has(lower)) {
					// Store the first encountered casing to display
					orgsMap.set(lower, u.org.trim());
				}
			}
		});
		apps?.forEach(a => {
			if (a.org && a.org.trim() !== "") {
				const lower = a.org.trim().toLowerCase();
				if (!orgsMap.has(lower)) {
					orgsMap.set(lower, a.org.trim());
				}
			}
		});
		return Array.from(orgsMap.values()).sort((a, b) => a.localeCompare(b));
	}, [users, apps]);

	const totalStorageFormatted = useMemo(() => {
		if (!apps) return "0 MB";
		const totalBytes = apps.reduce((acc, a) => acc + (a.size || 0), 0);
		if (!totalBytes) return "0 MB";
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
		const i = Math.floor(Math.log(totalBytes) / Math.log(k));
		return `${parseFloat((totalBytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
	}, [apps]);

	if (!apps) return <Loading message="Loading courses..." />

	return (
		<Flex flexDir="column" w="100%" maxW={{ base: "100%", "2xl": "1600px" }} mx="auto" p={{ base: 4, md: 6 }}>
			{/* Banner Section */}
			<Flex
				w="100%"
				h="200px"
				bgGradient="linear(to-r, #0284c7, #0ea5e9, #06b6d4, #10b981)"
				borderRadius="2xl"
				mb={6}
				alignItems="center"
				justifyContent="center"
				flexDir="column"
				color="white"
				boxShadow="0 12px 32px rgba(14, 165, 233, 0.3)"
				position="relative"
				overflow="hidden"
			>
				{/* Background Glow */}
				<Box position="absolute" top="-30%" left="-10%" w="120%" h="160%" bgGradient="radial(circle, rgba(255,255,255,0.2) 0%, transparent 60%)" pointerEvents="none" />
				
				<AnimatePresence mode="wait">
					{showAnimation ? (
						<motion.div
							key="xnav-anim"
							initial={{ scale: 0.8, opacity: 0, filter: "blur(10px)" }}
							animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
							exit={{ scale: 1.2, opacity: 0, filter: "blur(10px)" }}
							transition={{ duration: 1.2, ease: "easeInOut" }}
							style={{ position: "absolute", display: "flex", alignItems: "center", justifyContent: "center" }}
						>
							<Heading size="4xl" fontWeight="black" textTransform="uppercase" letterSpacing="widest" color="white" textShadow="0px 4px 25px rgba(0,0,0,0.2)">
								XNav
							</Heading>
						</motion.div>
					) : (
						<motion.div
							key="teach-today"
							initial={{ y: 30, opacity: 0 }}
							animate={{ y: 0, opacity: 1 }}
							transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
							style={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center" }}
						>
							<Heading 
								size="2xl" 
								mb={2} 
								fontWeight="black" 
								color="white"
								textShadow="0 4px 20px rgba(0, 0, 0, 0.2)"
							>
								What will you teach today?
							</Heading>
							{broadcast && broadcast.isActive && (
								<motion.div
									initial={{ opacity: 0, scale: 0.9 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
									style={{
										marginTop: '16px',
										padding: '8px 24px',
										background: 'rgba(255, 255, 255, 0.25)',
										backdropFilter: 'blur(10px)',
										borderRadius: '999px',
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										border: '1px solid rgba(255, 255, 255, 0.5)',
										boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
									}}
								>
									<FontAwesomeIcon icon={faBullhorn} color="white" />
									<Text fontWeight="bold" color="white" fontSize="md">{broadcast.message}</Text>
								</motion.div>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</Flex>

			{/* Course View Mode Tabs (Active Courses vs Archive) & Primary Desktop Action */}
			<Flex 
				justify="space-between" 
				align="flex-end" 
				borderBottom="1px solid" 
				borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.08)")} 
				mb={3}
				flexWrap="wrap" 
				gap={3}
			>
				<Tabs
					variant="line"
					index={courseTab === 'active' ? 0 : 1}
					onChange={(index) => setCourseTab(index === 0 ? 'active' : 'archive')}
					mb="-1px"
				>
					<TabList borderBottom="none" gap={4}>
						<Tab
							fontSize="sm"
							fontWeight="bold"
							py={2.5}
							px={2}
							borderBottom="2px solid transparent"
							color={courseTab === 'active' ? useColorModeValue("gray.900", "white") : useColorModeValue("gray.500", "gray.400")}
							_selected={{
								color: useColorModeValue("#0284c7", "#38bdf8"),
								borderBottom: "2px solid #0ea5e9",
							}}
							_hover={{
								color: useColorModeValue("gray.800", "white"),
							}}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							display="inline-flex"
							alignItems="center"
							gap={2.5}
						>
							<FontAwesomeIcon icon={faBookOpen} style={{ fontSize: '13px' }} />
							<Text>Active Courses</Text>
							<Badge
								fontSize="11px"
								fontWeight="bold"
								borderRadius="full"
								px={2}
								py={0.5}
								bg={courseTab === 'active' ? "rgba(14, 165, 233, 0.16)" : useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)")}
								color={courseTab === 'active' ? "#38bdf8" : useColorModeValue("gray.500", "gray.400")}
							>
								{activeAppsCount}
							</Badge>
						</Tab>
						<Tab
							fontSize="sm"
							fontWeight="bold"
							py={2.5}
							px={2}
							borderBottom="2px solid transparent"
							color={courseTab === 'archive' ? useColorModeValue("gray.900", "white") : useColorModeValue("gray.500", "gray.400")}
							_selected={{
								color: useColorModeValue("orange.600", "#fbbf24"),
								borderBottom: "2px solid #f59e0b",
							}}
							_hover={{
								color: useColorModeValue("gray.800", "white"),
							}}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							display="inline-flex"
							alignItems="center"
							gap={2.5}
						>
							<FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: '13px' }} />
							<Text>Archive / Bin</Text>
							<Badge
								fontSize="11px"
								fontWeight="bold"
								borderRadius="full"
								px={2}
								py={0.5}
								bg={courseTab === 'archive' ? "rgba(245, 158, 11, 0.16)" : useColorModeValue("gray.100", "rgba(255, 255, 255, 0.06)")}
								color={courseTab === 'archive' ? "#fbbf24" : useColorModeValue("gray.500", "gray.400")}
							>
								{archivedAppsCount}
							</Badge>
						</Tab>
					</TabList>
				</Tabs>

				{courseTab === 'active' && userRole !== 'Reviewer' && (
					<Button
						mb={2}
						size="sm"
						h="34px"
						bgGradient="linear(to-r, #0284c7, #0ea5e9)"
						_hover={{
							bgGradient: "linear(to-r, #0369a1, #0284c7)",
							transform: "translateY(-1px)",
							shadow: "0 4px 14px rgba(14, 165, 233, 0.35)"
						}}
						_active={{ transform: "translateY(0)" }}
						color="white"
						leftIcon={<FontAwesomeIcon icon={faPlus} />}
						onClick={() => window.dispatchEvent(new CustomEvent('open-create-course-modal'))}
						borderRadius="lg"
						px={4}
						fontWeight="bold"
						fontSize="xs"
						shadow="sm"
						transition="all 0.2s"
					>
						Create Course
					</Button>
				)}
			</Flex>

			{/* Premium Search and Filter Bar */}
			<Flex 
				w="100%" 
				bg={useColorModeValue("white", "#101622")} 
				p={2} 
				pl={3} 
				borderRadius="xl" 
				border="1px solid" 
				borderColor={useColorModeValue("gray.200", "rgba(255, 255, 255, 0.06)")} 
				flexDir={{ base: "column", md: "row" }} 
				alignItems="center" 
				gap={2}
				mb={4}
			>
				{/* Search Input */}
				<InputGroup size="sm" flex={1}>
					<InputLeftElement pointerEvents="none" h="full" color={useColorModeValue("gray.400", "gray.500")} pl={2}>
						<FontAwesomeIcon icon={faSearch} />
					</InputLeftElement>
					<Input
						placeholder="Search courses by title, instructor, or tags..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						bg="transparent"
						border="none"
						_focus={{ boxShadow: "none" }}
						_focusVisible={{ outline: "none", boxShadow: "none" }}
						color={useColorModeValue("gray.800", "white")}
						fontSize="sm"
						fontWeight="medium"
						pl={9}
					/>
				</InputGroup>

				<Flex gap={2} w={{ base: "100%", md: "auto" }} flexWrap="wrap" alignItems="center" pr={2}>
					{/* Organization Filter */}
					<Menu isLazy>
						<MenuButton 
							as={Button} 
							variant="ghost" 
							borderRadius="lg" 
							size="sm"
							leftIcon={<FontAwesomeIcon icon={faFilter} color="#0ea5e9" style={{ fontSize: '12px' }} />}
							fontWeight="600"
							fontSize="xs"
							color={useColorModeValue("gray.700", "gray.200")}
							_hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)") }}
							_active={{ bg: useColorModeValue("rgba(14, 165, 233, 0.12)", "rgba(14, 165, 233, 0.18)") }}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							transition="all 0.15s ease"
						>
							{orgFilter === "All" ? "All Organizations" : orgFilter}
						</MenuButton>
						<Portal>
							<MenuList 
								shadow="xl" 
								borderRadius="xl" 
								border="1px solid"
								borderColor={useColorModeValue("gray.200", "#374151")}
								overflow="hidden" 
								py={1} 
								bg={useColorModeValue("white", "#111827")} 
								p={1.5} 
								zIndex={1500}
								_focus={{ outline: "none" }}
							>
								<Box px={2} py={1.5} borderBottom="1px solid" borderColor={useColorModeValue("gray.100", "#1f2937")} mb={1}>
									<InputGroup size="xs">
										<InputLeftElement pointerEvents="none">
											<FontAwesomeIcon icon={faSearch} color="#9ca3af" />
										</InputLeftElement>
										<Input
											placeholder="Search orgs..."
											value={orgSearchQuery}
											onChange={(e) => setOrgSearchQuery(e.target.value)}
											onClick={(e) => e.stopPropagation()}
											onKeyDown={(e) => e.stopPropagation()}
											bg={useColorModeValue("gray.50", "#1f2937")}
											border="1px solid"
											borderColor={useColorModeValue("gray.200", "#374151")}
											borderRadius="md"
											fontSize="xs"
											_focus={{ borderColor: "#0ea5e9", boxShadow: "none" }}
											_focusVisible={{ outline: "none" }}
										/>
									</InputGroup>
								</Box>
								<MenuItem 
									py={2} 
									px={3}
									borderRadius="md" 
									fontSize="xs"
									fontWeight={orgFilter === "All" ? "700" : "500"} 
									bg={orgFilter === "All" ? useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "rgba(14, 165, 233, 0.18)") : "transparent"}
									color={orgFilter === "All" ? "#0284c7" : useColorModeValue("gray.800", "gray.200")}
									_hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
									_focus={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), outline: "none" }}
									_focusVisible={{ outline: "none", boxShadow: "none" }}
									onClick={() => {
										setOrgFilter("All");
										setOrgSearchQuery("");
									}}
								>
									All Organizations
								</MenuItem>
								{uniqueOrgs
									.filter(org => org.toLowerCase().includes(orgSearchQuery.toLowerCase()))
									.map(org => (
										<MenuItem 
											key={org} 
											py={2} 
											px={3}
											borderRadius="md" 
											fontSize="xs"
											fontWeight={orgFilter === org ? "700" : "500"} 
											bg={orgFilter === org ? useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "rgba(14, 165, 233, 0.18)") : "transparent"}
											color={orgFilter === org ? "#0284c7" : useColorModeValue("gray.800", "gray.200")}
											_hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
											_focus={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), outline: "none" }}
											_focusVisible={{ outline: "none", boxShadow: "none" }}
											onClick={() => {
												setOrgFilter(org);
												setOrgSearchQuery("");
											}}
										>
											{org}
										</MenuItem>
								))}
							</MenuList>
						</Portal>
					</Menu>

					<Box w="1px" h="18px" bg={useColorModeValue("gray.200", "#374151")} mx={1} display={{ base: "none", md: "block" }} />

					{/* Sort Options */}
					<Menu isLazy>
						<MenuButton 
							as={Button} 
							variant="ghost" 
							borderRadius="lg" 
							size="sm"
							leftIcon={<FontAwesomeIcon icon={faSortAmountDown} color="#0ea5e9" style={{ fontSize: '12px' }} />}
							fontWeight="600"
							fontSize="xs"
							color={useColorModeValue("gray.700", "gray.200")}
							_hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)") }}
							_active={{ bg: useColorModeValue("rgba(14, 165, 233, 0.12)", "rgba(14, 165, 233, 0.18)") }}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							transition="all 0.15s ease"
						>
							Sort by {sortOptionLabels[sortOption] || sortOption}
						</MenuButton>
						<Portal>
							<MenuList 
								shadow="xl" 
								borderRadius="xl" 
								border="1px solid"
								borderColor={useColorModeValue("gray.200", "#374151")}
								overflow="hidden" 
								py={1} 
								bg={useColorModeValue("white", "#111827")} 
								p={1.5} 
								zIndex={1500}
								_focus={{ outline: "none" }}
							>
								{Object.keys(sortOptionLabels).map((opt) => (
									<MenuItem 
										key={opt} 
										py={2} 
										px={3}
										borderRadius="md" 
										fontSize="xs"
										fontWeight={sortOption === opt ? "700" : "500"} 
										bg={sortOption === opt ? useColorModeValue("linear-gradient(90deg, rgba(14, 165, 233, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)", "rgba(14, 165, 233, 0.18)") : "transparent"}
										color={sortOption === opt ? "#0284c7" : useColorModeValue("gray.800", "gray.200")}
										_hover={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), color: "#0284c7" }}
										_focus={{ bg: useColorModeValue("rgba(14, 165, 233, 0.08)", "rgba(14, 165, 233, 0.12)"), outline: "none" }}
										_focusVisible={{ outline: "none", boxShadow: "none" }}
										onClick={() => setSortOption(opt)}
									>
										Sort by {sortOptionLabels[opt]}
									</MenuItem>
								))}
							</MenuList>
						</Portal>
					</Menu>

					<Box w="1px" h="20px" bg={useColorModeValue("gray.200", "gray.700")} mx={1} display={{ base: "none", md: "block" }} />

					{/* View Toggle */}
					<Flex bg={useColorModeValue("gray.100", "rgba(255, 255, 255, 0.04)")} p="3px" borderRadius="lg">
						<IconButton
							aria-label="List View"
							icon={<FontAwesomeIcon icon={faList} />}
							onClick={() => setViewMode('list')}
							variant="ghost"
							bg={viewMode === 'list' ? useColorModeValue("white", "rgba(255, 255, 255, 0.1)") : 'transparent'}
							color={viewMode === 'list' ? useColorModeValue("#0284c7", "#38bdf8") : useColorModeValue("gray.500", "gray.400")}
							shadow={viewMode === 'list' ? 'xs' : 'none'}
							_hover={{ color: useColorModeValue("gray.800", "white") }}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							borderRadius="md"
							size="sm"
							h="28px"
							w="28px"
							transition="all 0.2s"
						/>
						<IconButton
							aria-label="Grid View"
							icon={<FontAwesomeIcon icon={faThLarge} />}
							onClick={() => setViewMode('grid')}
							variant="ghost"
							bg={viewMode === 'grid' ? useColorModeValue("white", "rgba(255, 255, 255, 0.1)") : 'transparent'}
							color={viewMode === 'grid' ? useColorModeValue("#0284c7", "#38bdf8") : useColorModeValue("gray.500", "gray.400")}
							shadow={viewMode === 'grid' ? 'xs' : 'none'}
							_hover={{ color: useColorModeValue("gray.800", "white") }}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							borderRadius="md"
							size="sm"
							h="28px"
							w="28px"
							transition="all 0.2s"
						/>
						<IconButton
							aria-label="Landscape View"
							icon={<FontAwesomeIcon icon={faImage} />}
							onClick={() => setViewMode('landscape')}
							variant="ghost"
							bg={viewMode === 'landscape' ? useColorModeValue("white", "rgba(255, 255, 255, 0.1)") : 'transparent'}
							color={viewMode === 'landscape' ? useColorModeValue("#0284c7", "#38bdf8") : useColorModeValue("gray.500", "gray.400")}
							shadow={viewMode === 'landscape' ? 'xs' : 'none'}
							_hover={{ color: useColorModeValue("gray.800", "white") }}
							_focus={{ outline: "none", boxShadow: "none" }}
							_focusVisible={{ outline: "none", boxShadow: "none" }}
							borderRadius="md"
							size="sm"
							h="28px"
							w="28px"
							transition="all 0.2s"
						/>
					</Flex>
				</Flex>
			</Flex>

			<FuzzyFinder
				data={filteredAndSortedApps}
				searchKeys={['name']}
				query={searchQuery}
				RenderComponent={AppCard}
				RenderWrapper={viewMode === 'list' ? ListWrapper : GridWrapper}
				renderComponentProps={{ onDelete: deleteApp, onArchive: archiveApp, onUnarchive: unarchiveApp, onEdit: patchApp, onValidate: validateApp, viewMode: viewMode, userRole, userOrg, currentUserId: user.userId, currentUserEmail: user.signInDetails?.loginId }}
			>
				{courseTab === 'active' && <AddAppButton onAdd={postApp} onValidate={(app) => validateApp(app)} userRole={userRole} hideFab={true} />}
			</FuzzyFinder>
		</Flex>
	)
}





